import { useState, useEffect } from 'react';
import { X, CheckCircle, XCircle } from 'lucide-react';
import { useTranslation } from '../../i18n/hooks/useTranslation';

interface Question {
  id: string;
  question: string;
  options: string[];
  answer: number;
  explanation: string;
}

type QuizPhase =
  | { name: 'loading' }
  | { name: 'question'; questions: Question[]; index: number }
  | { name: 'answered'; questions: Question[]; index: number; selected: number }

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

interface QuizModalProps {
  isOpen: boolean;
  onClose: () => void;
  hasCompactTimer?: boolean;
}

export function QuizModal({ isOpen, onClose, hasCompactTimer = false }: QuizModalProps) {
  const { t, locale } = useTranslation();
  const [phase, setPhase] = useState<QuizPhase>({ name: 'loading' });
  const [score, setScore] = useState({ correct: 0, total: 0 });

  useEffect(() => {
    if (!isOpen) return;
    setPhase({ name: 'loading' });
    setScore({ correct: 0, total: 0 });
    const lang = locale === 'fr' ? 'fr' : 'en';
    fetch(`/quiz/rules.${lang}.json`)
      .then(r => r.json())
      .then((questions: Question[]) => {
        const shuffled = shuffle(questions);
        setPhase({ name: 'question', questions: shuffled, index: 0 });
      })
      .catch(() => setPhase({ name: 'question', questions: [], index: 0 }));
  }, [isOpen, locale]);

  if (!isOpen) return null;

  function select(optionIndex: number) {
    if (phase.name !== 'question') return;
    const isCorrect = optionIndex === phase.questions[phase.index].answer;
    setScore(s => ({ correct: s.correct + (isCorrect ? 1 : 0), total: s.total + 1 }));
    setPhase({ ...phase, name: 'answered', selected: optionIndex });
  }

  function next() {
    if (phase.name !== 'answered') return;
    const { questions, index } = phase;
    if (index < questions.length - 1) {
      setPhase({ name: 'question', questions, index: index + 1 });
    } else {
      // Restart with new shuffle
      setPhase({ name: 'question', questions: shuffle(questions), index: 0 });
    }
  }

  const q = phase.name !== 'loading' && phase.questions.length > 0 ? phase.questions[phase.index] : null;

  return (
    <div
      className={`fixed inset-0 z-[55] bg-black bg-opacity-50 flex items-center justify-center ${hasCompactTimer ? 'pt-24' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="quiz-title"
    >
      <div className="bg-white flex flex-col overflow-hidden" style={{ width: '95%', height: '85vh' }}>

        {/* Header */}
        <div className="bg-gray-800 text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
          <span id="quiz-title" className="font-semibold text-sm">{t('quiz.title')}</span>
          <button
            onClick={onClose}
            aria-label={t('common.close')}
            className="text-white p-1 hover:bg-gray-700 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Score bar */}
        {q && (
          <div className="flex-shrink-0 px-4 pt-3 pb-1">
            <div className="flex items-center gap-2">
              <div className={`flex-1 h-1.5 rounded-full overflow-hidden ${score.total === 0 ? 'bg-gray-200' : 'bg-red-300'}`}>
                {score.total > 0 && (
                  <div
                    className="h-full bg-green-300 transition-all duration-300 rounded-full"
                    style={{ width: `${(score.correct / score.total) * 100}%` }}
                  />
                )}
              </div>
              {score.total > 0 && (
                <span className="text-xs text-gray-500 font-mono whitespace-nowrap">
                  {score.correct}/{score.total}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 flex flex-col">
          {phase.name === 'loading' && (
            <div className="flex-1 flex items-center justify-center text-gray-400">{t('quiz.loading')}</div>
          )}

          {phase.name !== 'loading' && phase.questions.length === 0 && (
            <div className="flex-1 flex items-center justify-center text-gray-400">{t('quiz.noQuestions')}</div>
          )}

          {q && (
            <>
              <p className="text-gray-800 font-semibold text-base mb-4 leading-snug">{q.question}</p>
              <div className="flex flex-col gap-2">
                {q.options.map((opt, i) => {
                  const isAnswered = phase.name === 'answered';
                  const isCorrect = i === q.answer;
                  const isSelected = isAnswered && (phase as { selected: number }).selected === i;

                  let cls = 'px-4 py-3 text-left text-sm border-2 transition-colors ';
                  if (!isAnswered) {
                    cls += 'border-gray-200 bg-gray-50 hover:border-gray-400 cursor-pointer';
                  } else if (isCorrect) {
                    cls += 'border-green-500 bg-green-50 text-green-800';
                  } else if (isSelected) {
                    cls += 'border-red-400 bg-red-50 text-red-800';
                  } else {
                    cls += 'border-gray-100 bg-gray-50 text-gray-400';
                  }

                  return isAnswered ? (
                    <div key={i} className={cls}>{opt}</div>
                  ) : (
                    <button key={i} className={cls} onClick={() => select(i)}>{opt}</button>
                  );
                })}
              </div>

              {phase.name === 'answered' && (
                <div className="mt-4 flex flex-col gap-3">
                  <div className={`flex items-center gap-2 font-semibold text-sm ${phase.selected === q.answer ? 'text-green-600' : 'text-red-600'}`}>
                    {phase.selected === q.answer
                      ? <><CheckCircle size={18} /> {t('quiz.correct')}</>
                      : <><XCircle size={18} /> {t('quiz.wrong')}</>
                    }
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed">{q.explanation}</p>
                  <button
                    onClick={next}
                    className="mt-2 bg-gray-800 text-white px-6 py-3 font-semibold hover:bg-gray-700 transition-colors self-start"
                  >
                    {t('quiz.next')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
